import SplitLines from '../../assets/javascripts/4-helper/SplitLines.js';

export default class AnimationScrollPage {
  constructor() {
    this.$el = $('.animation');
    this.addClassAni = 'set-animation';
    this.splitDone = false;
    this.observer = null;
  }

  async init() {
    if (!this.splitDone) {
      await document.fonts.ready;
      new SplitLines().split();
      this.$el = $('.animation');
      this.splitDone = true;
    }
    this.initCountSpans();
    this.createObserver();
    this.observe(this.$el.toArray());

    // Re-check after all resources load — images may shift layout after fonts.ready,
    // causing in-viewport elements to be missed on the initial pass.
    window.addEventListener('load', () => this.observe(this.$el.toArray()), { once: true });
  }

  // Set all count spans to "0" on load so they don't flash the real number before animating
  initCountSpans() {
    document.querySelectorAll('.anima-count-number').forEach((el) => {
      const val = el.getAttribute('data-number') || el.textContent.trim();
      el.setAttribute('data-number', val);
      el.textContent = '0';
    });
  }

  // Trigger count-up for any .anima-count-number spans inside the animated container
  triggerCountUp(container) {
    container.querySelectorAll('.anima-count-number:not([data-counted])').forEach((el) => {
      el.setAttribute('data-counted', '1');

      const raw = el.getAttribute('data-number') || '';
      // Parse: optional prefix ($), digits, optional suffix (+, %, B, M, K…)
      const match = raw.match(/^([^0-9]*)(\d[\d,.]*)(.*)$/);
      if (!match) { el.textContent = raw; return; }

      const prefix = match[1];
      const numStr = match[2].replace(/,/g, '');
      const suffix = match[3];
      const endVal = parseFloat(numStr);
      const isInt = !numStr.includes('.');
      const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 2800;

      if (duration === 0) { el.textContent = raw; return; }

      const startTime = performance.now();
      const easeOut = (t) => 1 - (1 - t) ** 3;

      const tick = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        const current = easeOut(progress) * endVal;
        el.textContent = prefix + (isInt ? Math.round(current) : current.toFixed(1)) + suffix;
        if (progress < 1) requestAnimationFrame(tick);
        else el.textContent = raw; // restore exact original (e.g. "60+", "$50B")
      };

      requestAnimationFrame(tick);
    });
  }

  createObserver() {
    if (this.observer) this.observer.disconnect();

    // Fine-grained thresholds so callback fires often enough to catch the exact 50% point
    const thresholds = Array.from({ length: 21 }, (_, i) => i * 0.05);

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const elH = entry.boundingClientRect.height;
          const vH = entry.rootBounds ? entry.rootBounds.height : window.innerHeight;

          // Large viewport → need more element visible before firing (fire later).
          // Small viewport → fire earlier (element fills more of screen anyway).
          // Maps: vH ≤ 400px → 15%, vH = 900px → 45%, capped at 50%.
          const elementRatio = Math.max(0.10, Math.min(0.30, 0.10 + (vH - 400) / 500 * 0.20));
          const targetPx = Math.min(elH * elementRatio, vH * 0.5);

          if (entry.intersectionRect.height >= targetPx) {
            const el = entry.target;
            $(el).addClass(this.addClassAni);
            this.triggerCountUp(el);
            this.observer.unobserve(el);
          }
        });
      },
      { threshold: thresholds },
    );
  }

  observe(elements) {
    const vH = window.innerHeight;
    // On initial load, reveal an element only once it occupies a meaningful part of
    // the screen — at least this fraction of the viewport, or fully visible. Elements
    // merely peeking at the bottom edge are deferred to the scroll observer so they
    // animate in as the user reaches them (not prematurely).
    const REVEAL_VIEWPORT_RATIO = 0.2;

    elements.forEach((el) => {
      if ($(el).hasClass(this.addClassAni)) return;
      const rect = el.getBoundingClientRect();

      // Completely outside viewport — hand off to IntersectionObserver (scroll reveal)
      if (rect.bottom <= 0 || rect.top >= vH) {
        this.observer.observe(el);
        return;
      }

      const visibleH = Math.min(rect.bottom, vH) - Math.max(rect.top, 0);
      const targetPx = Math.min(rect.height, vH * REVEAL_VIEWPORT_RATIO);

      if (visibleH >= targetPx) {
        $(el).addClass(this.addClassAni);
        this.triggerCountUp(el);
      } else {
        // Only peeking in — let the observer fire once enough is shown on scroll
        this.observer.observe(el);
      }
    });
  }

  refresh() {
    this.observe($('.animation').toArray());
  }
}

const animationScrollPage = new AnimationScrollPage();
animationScrollPage.init();

window.animationScrollPage = animationScrollPage;

window.addEventListener('page-layout-change', () => {
  animationScrollPage.refresh();
});
